import styles from "./ProductSection.module.css"
import { useState, useEffect } from 'react';
import SecondaryButton from "../../../../components/Button/SecondaryButton/SecondaryButton";
import { LayoutGrid, List, ChevronLeft, ChevronRight} from 'lucide-react';
import { ProductTypes, ProductSectionProps, FilterTypes } from "../../../../types/product.types";
import CardRenderer from "../../../../components/ProductCards/CardRenderer";
import { useTranslation, Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useFilterContext } from "../../../../context/FilterContext";
import { useOrderContext } from "../../../../context/OrderContext";

/**
* A Product section component.
* Contains all the given products.
* Handles pagination, whether to show all products or just a sample and layout 
*
* @param props NewsProps
* @param {string} props.title - Title
* @param {any[]} props.products - Products
* @param {FilterTypes} props.type - Type of products
* @param {"list" | "block"} props.viewStyle - Layout style
* @param {(value: string) => void} props.setViewStyle - Handle layout change
* @param {boolean} props.showButtons - Whether to show buttons for layout change
* @param {(value: FilterTypes) => void} props.handleFilterSelect - Handle whether to show all or sample
* @param {number} props.limit - Limit of posts
* @param {boolean} props.single - Whether to show only one type of products
* @param {boolean} props.loading - Whether the products are still loading
* @returns TSX.Element
*/
const ProductSection = ({ title, products, type, viewStyle, showButtons, setViewStyle, handleFilterSelect, limit= 16, single, loading }: ProductSectionProps) => {
    const { t } = useTranslation(); // Translation
    const [page, setPage] = useState<number>(1) // Set page
    const productCount = products.length // Number of products

    // Set page back to 1 on filter or selected date change
    const { orderData } = useOrderContext();
    const { filterData } = useFilterContext();
    // Set Page
    useEffect(()=>{
        if(single){
            setPage(1);
        }
    },[filterData])
    // Set page and scroll to top
    useEffect(()=>{
        if(single){
            setPage(1);
            goToTopOfProduct();
        }
    },[orderData.selectedDate])

    // Scroll to top on page change
    const goToTopOfProduct = () => {
        document.getElementById('ProductSectionTop')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    
    if (products.length === 0){
        return(
            <>
            <div className={styles.ProductSection} >
                <div className={styles.ProductSectionHeader}>
                    <div className={styles.ProductSectionTitle} id={single ? "ProductSectionTop" : `ProductSectionTop-${type}`}>
                        <p>{title}</p>
                    </div>
                    {showButtons && (
                    <div className={styles.ViewSwitchButtons}>
                        <button onClick={()=> setViewStyle("list")} className={viewStyle == "list" ? `${styles.ViewSwitchButton} ${styles.SelectedView}`: `${styles.ViewSwitchButton}`}>
                            <List />
                        </button>
                        <button onClick={()=> setViewStyle("block")} className={viewStyle == "block" ? `${styles.ViewSwitchButton} ${styles.SelectedView}`: `${styles.ViewSwitchButton}`}>
                            <LayoutGrid />
                        </button>
                    </div>
                    )}
                </div>
                {loading && loading === true ? ( // Handle loading
                    <div className={styles.ProductSectionContent}>
                        <p className={styles.NotFoundText}>
                            {t("products.loading")}
                        </p>
                    </div>
                ):(
                    <div className={styles.ProductSectionContent}>
                        <p className={styles.NotFoundText}>      
                            {type === "storage" ? (
                                <Trans i18nKey="products.not_found_extra" components={{ 1: <br/> }} />
                            ):(
                                <Trans i18nKey="products.not_found" components={{ 1: <br/> }} />
                                )}
                            <a href="mailto:catermat@catermat.com" target="_blank">catermat@catermat.com</a>
                        </p>
                    </div>
                )}
            </div>
            </>
        )
    }

    return(
        <>
        <div className={styles.ProductSection} >
            <div className={styles.ProductSectionHeader}>
                <div className={styles.ProductSectionTitle} id={single ? "ProductSectionTop" : `ProductSectionTop-${type}`}>
                    <p>{title}</p>
                </div>
                {showButtons && (
                <div className={styles.ViewSwitchButtons}>
                    <button onClick={()=> setViewStyle("list")} className={viewStyle == "list" ? `${styles.ViewSwitchButton} ${styles.SelectedView}`: `${styles.ViewSwitchButton}`}>
                        <List />
                    </button>
                    <button onClick={()=> setViewStyle("block")} className={viewStyle == "block" ? `${styles.ViewSwitchButton} ${styles.SelectedView}`: `${styles.ViewSwitchButton}`}>
                        <LayoutGrid />
                    </button>
                </div>
                )}
            </div>
            {/*Product list with cards in list form */}
            {viewStyle == "block" ? (
            <>  
                <div className={`${styles.ProductSectionContent} ${styles.BlockList} ${single && styles.SingleBlocks}`}>
                    {products.map((product: ProductTypes, index) => {
                        if (limit && index >= limit * (page -1) && index<limit * page || limit == undefined) {
                            return (
                                <CardRenderer viewStyle={viewStyle} item={product} key={`${product.type}-${index}`} cardKey={`${product.type}-${index}`}/>
                            )
                        }})}
                        {!single && <SecondaryButton  text={single  ? t("buttons.back") : t("buttons.more")}  onClick={()=>handleFilterSelect(type)} className={viewStyle == "block" ? `${styles.MoreButtonBlock}` : `${styles.MoreButton}` } colorVariant="primary"/>}
                </div>
                 {/*Pagination */}
                {single && page === 1 && (
                    <div className={styles.Pagination}>
                        <button className={styles.PaginationChevron} onClick={() => { page > 1 && setPage(page - 1); goToTopOfProduct() }}>
                            <ChevronLeft/>
                        </button>
                        <button className={`${styles.PaginationItem} ${styles.SelectedPaginationItem}`}>
                            <p>1 </p>
                        </button>
                        {limit  *  (page ) <= productCount && (
                            <button className={styles.PaginationItem} onClick={()=>{setPage(page+1); goToTopOfProduct()}}>
                                <p>{page + 1}</p>
                            </button>
                        )}
                        {limit  *  (page+1) <= productCount && (
                            <button className={styles.PaginationItem} onClick={()=>{setPage(page+2); goToTopOfProduct()}}>
                                <p>{page+2}</p>
                            </button>
                        )}
                        <button className={styles.PaginationChevron} onClick={() => { limit * (page) <= productCount && setPage(page + 1); goToTopOfProduct() }}>
                            <ChevronRight/>
                        </button>
                    </div>
                )}
                {single && page > 1 && (
                    <div className={styles.Pagination}>
                        <button className={styles.PaginationChevron} onClick={() => { page > 1 && setPage(page - 1);  goToTopOfProduct() }}>
                            <ChevronLeft/>
                        </button>
                        <button className={styles.PaginationItem} onClick={()=>{setPage(page-1); goToTopOfProduct()}}>
                            <p>{page-1}</p>
                        </button>
                        <button className={`${styles.PaginationItem} ${styles.SelectedPaginationItem}`}>
                            <p>{page}</p>
                        </button>
                        {limit  *  (page) <= productCount && (
                            <button className={styles.PaginationItem} onClick={()=>{setPage(page+1); goToTopOfProduct()}}>
                                <p>{page+1}</p>
                            </button>
                        )}
                        <button className={styles.PaginationChevron} onClick={() => { limit * (page) <= productCount && setPage(page + 1); goToTopOfProduct() }}>
                            <ChevronRight/>
                        </button>
                    </div>
                )}
            </>
            ):(
            <>
                <div className={styles.ProductSectionContent}>
                {products.map((product: ProductTypes, index) => {
                    if (limit && index >= limit * (page -1) && index<limit * page ||  limit == undefined) {
                        return (
                            <CardRenderer viewStyle={viewStyle} item={product} cardKey={`${product.type}-${index}`} key={index}/>
                        )
                    }})}
                {!single && <SecondaryButton  text={single  ? t("buttons.back") : t("buttons.more")} onClick={()=>handleFilterSelect(type)} className={styles.MoreButton} colorVariant="primary"/>}
                </div>
                {/*Pagination */}
                {single && page === 1 && (
                    <div className={styles.Pagination}>
                        <button className={styles.PaginationChevron} onClick={() => { page > 1 && setPage(page - 1); goToTopOfProduct() }}>
                            <ChevronLeft/>
                        </button>
                        <button className={`${styles.PaginationItem} ${styles.SelectedPaginationItem}`}>
                            <p>1</p>
                        </button>
                        {limit  *  page <= productCount && (
                            <button className={styles.PaginationItem} onClick={()=>{setPage(page+1); goToTopOfProduct()}}>
                                <p>{page+1}</p>
                            </button>
                        )}
                        {limit  *  (page+1) <= productCount && (
                            <button className={styles.PaginationItem} onClick={()=>{setPage(page+2); goToTopOfProduct()}}>
                                <p>{page+2}</p>
                            </button>
                        )}
                        <button className={styles.PaginationChevron} onClick={() => { limit * (page) <= productCount && setPage(page + 1); goToTopOfProduct() }}>
                            <ChevronRight/>
                        </button>
                    </div>
                )}
                {single && page > 1 && (
                    <div className={styles.Pagination}>
                        <button className={styles.PaginationChevron} onClick={() => { page > 1 && setPage(page - 1); goToTopOfProduct() }}>
                            <ChevronLeft/>
                        </button>
                        <button className={styles.PaginationItem} onClick={() => {setPage(page - 1); goToTopOfProduct()}}>
                            <p>{page-1}</p>
                        </button>
                        <button className={`${styles.PaginationItem} ${styles.SelectedPaginationItem}`}>
                            <p>{page}</p>
                        </button>
                        {limit  *  page <= productCount && (
                            <button className={styles.PaginationItem} onClick={()=>{setPage(page+1); goToTopOfProduct()}}>
                                <p>{page+1}</p>
                            </button>
                        )}
                        <button className={styles.PaginationChevron} onClick={() => { limit * (page) <= productCount && setPage(page + 1); goToTopOfProduct() }}>
                            <ChevronRight/>
                        </button>
                    </div>
                )}
            </>
            )}
        </div>
        </>
    )
}

export default ProductSection
